import {
  Bell,
  BookOpen,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  Search,
  Sparkles,
  UsersRound,
  Video,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { LoadingState } from '../../components/common/LoadingState';
import { getCourse, getPublicAcademyBySlug, getStudentCourseSignups, getStudentVisibleClasses, getTenant } from '../../firebase/firestore';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../contexts/ToastContext';
import { getHostnameContext } from '../../utils/hostname';
import { buildClassAccessPath, formatClassSchedule } from '../../utils/classSchedule';
import { StudentSidebar } from './StudentSidebar';

const MODES = {
  online: { label: 'Virtual class', icon: Video, tone: 'blue' },
  physical: { label: 'On campus', icon: MapPin, tone: 'red' },
  hybrid: { label: 'Hybrid class', icon: UsersRound, tone: 'violet' },
  self_paced: { label: 'Self-paced', icon: BookOpen, tone: 'gold' },
};

function modeFor(classItem) {
  return MODES[classItem.locationType || 'online'] || MODES.online;
}

function formatTime(value) {
  const [hoursPart, minutesPart = '00'] = String(value || '').split(':');
  const hours = Number(hoursPart);
  if (!Number.isFinite(hours)) return '';
  return `${hours % 12 || 12}:${minutesPart} ${hours >= 12 ? 'PM' : 'AM'}`;
}

function classMatchesSession(classItem, signup, courseClasses) {
  if (courseClasses.length <= 1) return true;
  // Classes created from the classes page are course-associated by default.
  // If no intake session was explicitly selected, show them for every
  // enrolled learner in that course instead of hiding the class.
  if (!classItem.intakeSessionId && !classItem.intakeSessionLabel) return true;
  const selectedSession = String(signup.intakeSessionLabel || '').trim().toLowerCase();
  if (!selectedSession || selectedSession === 'open session') return true;
  if (classItem.intakeSessionId && signup.intakeSessionId) return classItem.intakeSessionId === signup.intakeSessionId;
  if (classItem.intakeSessionLabel && classItem.intakeSessionLabel.toLowerCase() === selectedSession) return true;
  const start = formatTime(classItem.startTime).toLowerCase();
  const end = formatTime(classItem.endTime).toLowerCase();
  return Boolean(start && end && selectedSession.includes(start) && selectedSession.includes(end));
}

function classDay(classItem) {
  if (classItem.scheduledDate) {
    const date = new Date(`${classItem.scheduledDate}T12:00:00`);
    if (!Number.isNaN(date.getTime())) return {
      day: new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(date),
      date: date.getDate(),
    };
  }
  const firstDay = classItem.recurrence?.daysOfWeek?.[0];
  return { day: firstDay ? firstDay.slice(0, 3) : 'Any', date: '—' };
}

export function StudentClassesPage() {
  const { user, loading: authLoading, logout } = useAuth();
  const { notify } = useToast();
  const hostnameContext = useMemo(() => getHostnameContext(), []);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [hasAdmittedStudentStatus, setHasAdmittedStudentStatus] = useState(false);
  const [academyName, setAcademyName] = useState('Student workspace');
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [modeFilter, setModeFilter] = useState('all');

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!user?.uid) {
        if (mounted) setLoading(false);
        return;
      }
      try {
        const allSignups = await getStudentCourseSignups(user.uid);
        let scoped = allSignups;
        if (hostnameContext.tenantSlug) {
          const academy = await getPublicAcademyBySlug(hostnameContext.tenantSlug);
          scoped = academy ? allSignups.filter((signup) => signup.tenantId === academy.id) : [];
        }
        const admittedSignups = scoped.filter((signup) => signup.status === 'student' || signup.status === 'admitted');
        if (mounted) setHasAdmittedStudentStatus(admittedSignups.length > 0);
        scoped = admittedSignups;
        const tenantIds = [...new Set(scoped.map((signup) => signup.tenantId || signup.ref?.parent?.parent?.id).filter(Boolean))];
        const classGroups = await Promise.all(tenantIds.map(async (tenantId) => {
          const tenant = await getTenant(tenantId).catch((tenantError) => {
            console.warn(`Unable to load academy ${tenantId}; continuing with student classes.`, tenantError);
            return null;
          });
          const tenantSignups = scoped.filter((signup) => (signup.tenantId || signup.ref?.parent?.parent?.id) === tenantId);
          const courseIds = [...new Set(tenantSignups.map((signup) => signup.courseId).filter(Boolean))];
          let tenantClasses = [];
          try {
            tenantClasses = await getStudentVisibleClasses(tenantId);
          } catch (classError) {
            // Admission stores the assigned class snapshot on the signup.
            // Use it when an older/deployed ruleset still blocks the class
            // collection query, so the student can still see their class.
            console.warn(`Unable to read public classes for academy ${tenantId}; using admitted assignments.`, classError);
          }
          const courseResults = await Promise.all(courseIds.map(async (courseId) => {
            try {
              return await getCourse(tenantId, courseId);
            } catch (courseError) {
              // A course can stop being publicly readable after signup. The
              // class is still valid for the enrolled student, so retain the
              // signup name and render the class instead of failing the page.
              console.warn(`Unable to load course ${courseId}; using signup details.`, courseError);
              return null;
            }
          }));
          const assignedClassFallbacks = tenantSignups
            .filter((signup) => signup.assignedClassId)
            .filter((signup) => !tenantClasses.some((item) => item.id === signup.assignedClassId))
            .map((signup) => ({
              id: signup.assignedClassId,
              title: signup.assignedClassTitle || signup.courseName || 'Class session',
              courseId: signup.courseId,
              startTime: signup.assignedClassStartTime || '',
              endTime: signup.assignedClassEndTime || '',
              locationType: signup.assignedClassLocationType || 'online',
              status: 'scheduled',
              publicAccess: { enabled: true },
              isAdmissionAssignment: true,
            }));
          tenantClasses = [...tenantClasses, ...assignedClassFallbacks];
          return tenantClasses.filter((classItem) => {
            const signup = tenantSignups.find((item) => item.courseId === classItem.courseId);
            const courseClasses = tenantClasses.filter((item) => item.courseId === classItem.courseId);
            const isAssignedClass = signup?.assignedClassId && signup.assignedClassId === classItem.id;
            return courseIds.includes(classItem.courseId)
              && (!signup?.assignedClassId || isAssignedClass)
              && classMatchesSession(classItem, signup || {}, courseClasses);
          }).map((classItem) => ({
            ...classItem,
            tenant,
            course: courseResults.find((course) => course?.id === classItem.courseId),
            signup: tenantSignups.find((signup) => signup.courseId === classItem.courseId),
          }));
        }));
        if (mounted) {
          const flattened = classGroups.flat();
          setClasses(flattened);
          setAcademyName(flattened[0]?.tenant?.name || (tenantIds.length ? 'My learning spaces' : 'Student workspace'));
          setLoading(false);
        }
      } catch (err) {
        console.error(err);
        if (mounted) {
          setError(err?.code === 'permission-denied'
            ? 'Your academy has not made this class available to student accounts yet.'
            : 'Unable to load your classes right now.');
          setLoading(false);
        }
      }
    }
    load();
    return () => { mounted = false; };
  }, [hostnameContext.tenantSlug, user?.uid]);

  async function onLogout() {
    await logout();
    notify('You have been signed out.');
  }

  if (authLoading) return <LoadingState label="Loading your classes..." />;
  if (!user) return <Navigate to="/student/login" replace />;

  const normalizedQuery = query.trim().toLowerCase();
  const filteredClasses = classes.filter((classItem) => {
    const matchesQuery = !normalizedQuery || [classItem.title, classItem.course?.name, classItem.tenant?.name, modeFor(classItem).label]
      .some((value) => String(value || '').toLowerCase().includes(normalizedQuery));
    const matchesMode = modeFilter === 'all'
      || classItem.locationType === modeFilter
      || (modeFilter === 'campus' && ['physical', 'hybrid'].includes(classItem.locationType));
    return matchesQuery && matchesMode;
  });
  const onlineCount = classes.filter((classItem) => classItem.locationType === 'online').length;
  const campusCount = classes.filter((classItem) => ['physical', 'hybrid'].includes(classItem.locationType)).length;
  const firstName = String(user.displayName || user.email || 'Student').split(/[ @]/)[0];
  const today = new Date();
  const dateLabel = new Intl.DateTimeFormat('en-GB', { day: '2-digit', month: 'long', year: 'numeric' }).format(today);
  const monthLabel = new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(today);
  const calendarDays = Array.from({ length: 5 }, (_, index) => {
    const date = new Date(today);
    date.setDate(today.getDate() + index - 2);
    return {
      date: date.getDate(),
      day: new Intl.DateTimeFormat('en-GB', { weekday: 'short' }).format(date),
      active: index === 2,
    };
  });

  return (
    <main className="student-portal student-classes-page">
      <StudentSidebar academyName={academyName} onLogout={onLogout} />

      <div className="student-classes-shell">
        <section className="student-classes-workspace">
          <header className="student-classes-page-header">
            <div>
              <span>Welcome back, {firstName}</span>
              <h1>My Classes</h1>
              <p>{dateLabel}</p>
            </div>
            <button type="button" className="student-dashboard-notification" aria-label="Notifications">
              <Bell size={19} strokeWidth={1.7} />
              <i />
            </button>
          </header>

          <div className="student-classes-toolbar-new">
            <label><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search your classes" /></label>
            <div className="student-class-filters" aria-label="Filter classes">
              <button className={modeFilter === 'all' ? 'is-active' : ''} type="button" onClick={() => setModeFilter('all')}>All</button>
              <button className={modeFilter === 'online' ? 'is-active' : ''} type="button" onClick={() => setModeFilter('online')}>Virtual</button>
              <button className={modeFilter === 'campus' ? 'is-active' : ''} type="button" onClick={() => setModeFilter('campus')}>Campus</button>
            </div>
          </div>

          <div className="student-classes-section-heading">
            <div><h2>Your class schedule</h2><p>Everything you need to attend and keep learning.</p></div>
            <span>{filteredClasses.length} class{filteredClasses.length === 1 ? '' : 'es'}</span>
          </div>

          {error ? <div className="student-dashboard-alert">{error}</div> : null}
          {loading ? (
            <div className="student-courses-inline-loading"><span /><p>Loading your classes...</p></div>
          ) : filteredClasses.length ? (
            <div className="student-classes-library">
              {filteredClasses.map((classItem) => {
                const mode = modeFor(classItem);
                const ModeIcon = mode.icon;
                const scheduleDay = classDay(classItem);
                const isSelfPaced = classItem.locationType === 'self_paced';
                return (
                  <article className={`student-schedule-card student-schedule-card--${mode.tone}`} key={`${classItem.tenant?.id}-${classItem.id}`}>
                    <div className="student-schedule-date"><span>{scheduleDay.day}</span><strong>{scheduleDay.date}</strong><small>{formatTime(classItem.startTime) || 'Flexible'}</small></div>
                    <div className="student-schedule-details">
                      <div className="student-schedule-topline"><span><ModeIcon size={13} />{mode.label}</span><i>{classItem.status || 'scheduled'}</i></div>
                      <h3>{classItem.title || classItem.course?.name || 'Class session'}</h3>
                      <p>{classItem.course?.name || classItem.signup?.courseName || 'Your enrolled course'}</p>
                      <div className="student-schedule-facts">
                        <span><Clock3 size={14} />{isSelfPaced ? `${classItem.selfPaced?.contentType || 'Course materials'} · Learn anytime` : formatClassSchedule(classItem)}</span>
                        {['physical', 'hybrid'].includes(classItem.locationType) ? <span><MapPin size={14} />{classItem.physicalLocation || 'Campus'}{classItem.room ? ` · ${classItem.room}` : ''}</span> : <span><Video size={14} />Online learning room</span>}
                      </div>
                      <div className="student-schedule-footer"><small>{classItem.tenant?.name || academyName}</small><Link to={buildClassAccessPath(classItem.tenant?.slug || '', classItem.id)}>Join class <ChevronRight size={15} /></Link></div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="student-courses-empty">
              <CalendarDays size={25} />
              <h2>{query || modeFilter !== 'all'
                ? 'No matching classes'
                : !hasAdmittedStudentStatus
                  ? 'Your class will be available after admission'
                  : 'No classes are ready yet'}</h2>
              <p>{query || modeFilter !== 'all'
                ? 'Try another search or choose a different class type.'
                : !hasAdmittedStudentStatus
                  ? 'Your class will be available when you have been admitted as a student.'
                  : 'Your classes will appear here as soon as your academy schedules them.'}</p>
              {query || modeFilter !== 'all' ? <button type="button" onClick={() => { setQuery(''); setModeFilter('all'); }}>Clear filters</button> : null}
            </div>
          )}
        </section>

        <aside className="student-classes-summary">
          <div className="student-classes-summary-heading"><h2>Class Overview</h2><CalendarDays size={17} /></div>

          <div className="student-classes-calendar">
            <header><button type="button" aria-label="Previous week"><ChevronLeft size={16} /></button><h3>{monthLabel}</h3><button type="button" aria-label="Next week"><ChevronRight size={16} /></button></header>
            <div>{calendarDays.map((day) => <span className={day.active ? 'is-active' : ''} key={`${day.day}-${day.date}`}><b>{day.date}</b><small>{day.day}</small></span>)}</div>
          </div>

          <div className="student-class-summary-stats">
            <article><span className="is-violet"><CalendarDays size={17} /></span><div><small>Total classes</small><strong>{classes.length}</strong></div></article>
            <article><span className="is-blue"><Video size={17} /></span><div><small>Virtual</small><strong>{onlineCount}</strong></div></article>
            <article><span className="is-red"><MapPin size={17} /></span><div><small>Campus & hybrid</small><strong>{campusCount}</strong></div></article>
          </div>

          <div className="student-class-tip">
            <span><Sparkles size={16} /></span>
            <small>Before class</small>
            <h3>Arrive ready to learn.</h3>
            <p>Open your course notes a few minutes before the session and write down one question.</p>
          </div>

          <Link className="student-courses-dashboard-link" to="/student/portal">Back to dashboard <ChevronRight size={16} /></Link>
        </aside>
      </div>
    </main>
  );
}
